import {
  STANDARD_PL_STRUCTURE,
  getStandardOrder,
  shouldShowGrowthRate,
  findStructureItem,
} from './standardPLStructure';

export interface PLRow {
  id: string;
  label: string;
  category: string;
  level: 0 | 1 | 2;
  values: Record<string, number>;
  growthRates?: Record<string, number>; // Year-over-year growth rates
  formula?: Record<string, string>;
  format?: 'currency' | 'percentage' | 'number';
  locked?: boolean;
  editable: boolean;
  showGrowthRate?: boolean;
}

interface RawFinancialData {
  node: {
    dbId: string;
    bmType: string;
    category1: string;
    category2?: string;
    category3?: string;
    value: number;
    period: number;
    periodType: string;
    currency: string;
    valuationLevel?: number;
  };
}

interface PLHierarchyConfig {
  label: string;
  level: number;
  subcategories?: Record<string, string>;
  formula?: string;
}

// P&L Category hierarchy and labels
const PL_HIERARCHY: Record<string, PLHierarchyConfig> = {
  sales: {
    label: 'Revenue',
    level: 0,
    subcategories: {
      saas: 'SaaS',
      'maintenance and support': 'Maintenance & Support',
      'hosting and rental': 'Hosting & Rental',
      licenses: 'Licenses',
      'third-party software': 'Third-party Software',
      consulting: 'Consulting',
      other: 'Other Revenue',
      'not classified': 'Not Classified',
      hardware: 'Hardware',
      total: 'Total Revenue',
    },
  },
  cogs: {
    label: 'Cost of Goods Sold',
    level: 0,
    subcategories: {
      total: 'Total COGS',
    },
  },
  'gross profit': {
    label: 'Gross Profit',
    level: 0,
    formula: 'sales - cogs',
  },
  opex: {
    label: 'Operating Expenses',
    level: 0,
    subcategories: {
      total: 'Total OPEX',
    },
  },
  pex: {
    label: 'Personnel Expenses',
    level: 0,
    subcategories: {
      'admin and finance': 'Admin & Finance',
      consulting: 'Consulting',
      devops: 'DevOps',
      'executive management': 'Executive Management',
      'product management': 'Product Management',
      'sales and marketing': 'Sales & Marketing',
      support: 'Support',
      total: 'Total Personnel',
    },
  },
  ebit: {
    label: 'EBIT',
    level: 0,
    formula: 'gross_profit - opex - pex',
  },
  ebitda: {
    label: 'EBITDA',
    level: 0,
    formula: 'ebit + da',
  },
  'd&a': {
    label: 'Depreciation & Amortization',
    level: 0,
  },
  ooi: {
    label: 'Other Operating Income',
    level: 0,
  },
};

// Helper function to format category names
const formatCategoryName = (category: string): string => {
  if (!category) return '';

  // Special cases
  const specialCases: Record<string, string> = {
    sales: 'Revenue',
    cogs: 'Cost of Goods Sold',
    'gross profit': 'Gross Profit',
    opex: 'Operating Expenses',
    pex: 'Personnel Expenses',
    ebit: 'EBIT',
    ebitda: 'EBITDA',
    'd&a': 'Depreciation & Amortization',
    ooi: 'Other Operating Income',
    fte: 'Full-Time Employees',
    saas: 'SaaS',
  };

  const lowerCategory = category.toLowerCase();
  if (specialCases[lowerCategory]) {
    return specialCases[lowerCategory];
  }

  // General formatting: capitalize words, replace underscores
  return category
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
    .replace(/And/g, '&');
};

export const processPLData = (rawData: any[], selectedValuationLevel?: number) => {
  const years = new Set<string>();
  const categoryData = new Map<string, Map<string, number>>();
  const categoryHierarchy = new Map<string, Set<string>>();
  const yearPeriodTypes = new Map<string, string>(); // Track period types for each year
  const currencies = new Set<string>(); // Track currencies in the data

  // Handle both direct array and edges/node structure
  const dataArray = rawData[0]?.node ? rawData : rawData[0]?.edges ? rawData[0].edges : [];

  if (dataArray.length === 0) {
    return { rows: [], years: [], currency: 'USD', yearPeriodTypes: new Map() };
  }

  // First pass: collect all data and build hierarchy
  const valuationData = new Map<string, { value: number; level: number }>();

  dataArray.forEach(item => {
    const nodeData = item.node || item;
    const { category1, category2, category3, value, period, valuationLevel = 0, periodType, currency } = nodeData;

    if (!category1 || period === undefined || period === null) {
      return;
    }

    const yearStr = period.toString();
    years.add(yearStr);

    // Track currency and period types
    if (currency) currencies.add(currency);
    if (periodType) {
      yearPeriodTypes.set(yearStr, periodType);
    }

    // Skip normalizations for now
    if (category3 === 'normalizations') {
      return;
    }

    // Only use 'total' entries for category3
    if (category3 && category3 !== 'total') {
      return;
    }

    // Build the category key
    let categoryKey = category1;

    // Track hierarchy
    if (!categoryHierarchy.has(category1)) {
      categoryHierarchy.set(category1, new Set());
    }

    // Handle main category totals
    if (category2 === 'total') {
      // This is a main category total
      categoryKey = category1;
    } else if (category2 && category2 !== 'total') {
      // This is a subcategory
      categoryKey = `${category1}|${category2}`;
      categoryHierarchy.get(category1)?.add(category2);
    }

    // Create unique key for valuation level handling
    const dataKey = `${categoryKey}|${yearStr}`;
    const existingData = valuationData.get(dataKey);

    // Filter by valuation level if specified
    if (selectedValuationLevel !== undefined) {
      if (valuationLevel === selectedValuationLevel) {
        valuationData.set(dataKey, { value: value || 0, level: valuationLevel });
      }
    } else {
      // If no specific level selected, use highest valuation level
      if (!existingData || valuationLevel > existingData.level) {
        valuationData.set(dataKey, { value: value || 0, level: valuationLevel });
      }
    }
  });

  // Convert valuation data to categoryData
  valuationData.forEach((data, key) => {
    const parts = key.split('|');
    const yearStr = parts.pop() as string;
    const categoryKey = parts.join('|');

    if (!categoryData.has(categoryKey)) {
      categoryData.set(categoryKey, new Map());
    }
    categoryData.get(categoryKey)?.set(yearStr, data.value);
  });

  const sortedYears = Array.from(years).sort();
  const rows: PLRow[] = [];

  // Determine primary currency (most common)
  const primaryCurrency = Array.from(currencies)[0] || 'USD';

  // Calculate growth rates helper function
  const calculateGrowthRates = (values: Record<string, number>, years: string[]): Record<string, number> => {
    const growthRates: Record<string, number> = {};
    const sortedYears = years.sort();

    for (let i = 1; i < sortedYears.length; i++) {
      const currentYear = sortedYears[i];
      const previousYear = sortedYears[i - 1];
      const currentValue = values[currentYear] || 0;
      const previousValue = values[previousYear] || 0;

      if (previousValue !== 0) {
        growthRates[currentYear] = (currentValue - previousValue) / Math.abs(previousValue);
      } else {
        growthRates[currentYear] = 0;
      }
    }

    return growthRates;
  };

  // Build rows from actual data using standardized structure
  const processedCategories = new Set<string>();

  // Create a map of available data categories for quick lookup
  const availableCategories = new Map<string, string>();
  categoryData.forEach((data, key) => {
    const parts = key.split('|');
    const cat1 = parts[0];
    availableCategories.set(cat1, key);
  });

  // Process categories in standardized order
  STANDARD_PL_STRUCTURE.forEach(structItem => {
    if (structItem.level !== 0) return; // Only process main categories here

    // Find matching data using key or aliases
    let matchingKey = '';
    let matchingData: Map<string, number> | undefined;

    // Try direct match first
    if (categoryData.has(structItem.key)) {
      matchingKey = structItem.key;
      matchingData = categoryData.get(structItem.key);
    } else {
      // Try aliases
      for (const alias of structItem.aliases || []) {
        if (categoryData.has(alias)) {
          matchingKey = alias;
          matchingData = categoryData.get(alias);
          break;
        }
      }
    }

    // If no direct match, check if we have data that starts with this category
    if (!matchingData) {
      const hasData = Array.from(categoryData.keys()).some(
        key => key.startsWith(structItem.key) || structItem.aliases?.some(alias => key.startsWith(alias))
      );
      if (!hasData) return;

      matchingKey = structItem.key;
      matchingData = categoryData.get(matchingKey);
    }

    const mainRow: PLRow = {
      id: matchingKey || structItem.key,
      label: structItem.label,
      category: structItem.key,
      level: 0,
      values: {},
      editable: !structItem.isCalculated,
      format: 'currency',
      showGrowthRate: structItem.showGrowthRate,
    };

    // Fill in values for main category
    sortedYears.forEach(year => {
      mainRow.values[year] = matchingData?.get(year) || 0;
    });

    // Calculate growth rates if needed
    if (structItem.showGrowthRate && sortedYears.length > 1) {
      mainRow.growthRates = calculateGrowthRates(mainRow.values, sortedYears);
    }

    rows.push(mainRow);
    processedCategories.add(matchingKey || structItem.key);

    // Add subcategories if they exist - iterate in the order defined in standardPLStructure
    if (structItem.subcategories && structItem.subcategories.length > 0) {
      structItem.subcategories.forEach(cat2Key => {
        const subKey = `${structItem.key}|${cat2Key}`;
        const subData = categoryData.get(subKey);

        // Only add row if we have data for this subcategory
        if (subData) {
          const subRow: PLRow = {
            id: subKey,
            label: `  ${formatCategoryName(cat2Key)}`,
            category: structItem.key,
            level: 1,
            values: {},
            editable: true,
            format: 'currency',
            showGrowthRate: structItem.showGrowthRate,
          };

          sortedYears.forEach(year => {
            subRow.values[year] = subData.get(year) || 0;
          });

          // Calculate growth rates for revenue subcategories
          if (structItem.showGrowthRate && sortedYears.length > 1) {
            subRow.growthRates = calculateGrowthRates(subRow.values, sortedYears);
          }

          rows.push(subRow);
          processedCategories.add(subKey);
        }
      });
    }

    // Add margin calculations for key metrics
    if (['gross profit', 'ebit', 'ebitda'].includes(structItem.key)) {
      const marginRow: PLRow = {
        id: `${structItem.key}_margin`,
        label: `${structItem.label} Margin %`,
        category: structItem.key,
        level: 1,
        values: {},
        editable: false,
        locked: true,
        format: 'percentage',
      };

      const salesData = categoryData.get('sales');
      sortedYears.forEach(year => {
        const revenue = salesData?.get(year) || 0;
        const value = mainRow.values[year] || 0;
        marginRow.values[year] = revenue !== 0 ? value / revenue : 0;
      });

      rows.push(marginRow);
    }
  });

  // Add any categories not in the predefined order
  categoryData.forEach((data, key) => {
    if (!processedCategories.has(key)) {
      const parts = key.split('|');
      const cat1 = parts[0];
      const cat2 = parts[1];

      const row: PLRow = {
        id: key,
        label: cat2 ? `  ${formatCategoryName(cat2)}` : formatCategoryName(cat1),
        category: cat1,
        level: cat2 ? 1 : 0,
        values: {},
        editable: true,
        format: 'currency',
      };

      sortedYears.forEach(year => {
        row.values[year] = data.get(year) || 0;
      });

      rows.push(row);
    }
  });

  return {
    rows,
    years: sortedYears,
    currency: primaryCurrency,
    yearPeriodTypes,
  };
};

const calculateFormula = (
  formula: string,
  dataMap: Map<string, Map<string, { value: number; valuationLevel: number }>>,
  year: string
): number => {
  // Simple formula parser for basic operations
  // Example: "sales - cogs" or "gross_profit - opex - pex"
  const parts = formula.split(/[+-]/);
  const operators = formula.match(/[+-]/g) || [];

  let result = 0;
  parts.forEach((part, index) => {
    const entry = dataMap.get(part.trim())?.get(year);
    const value = entry?.value || 0;
    if (index === 0) {
      result = value;
    } else {
      const operator = operators[index - 1];
      if (operator === '+') {
        result += value;
      } else if (operator === '-') {
        result -= value;
      }
    }
  });

  return result;
};

export const transformToSpreadsheetData = (rows: PLRow[], years: string[]): any[][] => {
  const spreadsheetData: any[][] = [];

  // Header row
  spreadsheetData.push(['Item', ...years]);

  // Data rows
  rows.forEach(row => {
    const rowData: any[] = [row.label];
    years.forEach(year => {
      const value = row.values[year] || 0;
      // Format based on row type
      if (row.format === 'percentage') {
        // For percentage, keep as decimal (0.25 for 25%)
        rowData.push(value);
      } else {
        // For currency values, round to 2 decimal places
        rowData.push(Math.round(value * 100) / 100);
      }
    });
    spreadsheetData.push(rowData);
  });

  return spreadsheetData;
};
